import { useMemo } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../../utils/tauri';
import { useVaultStore, guardNewVaultItems } from '../../store/vaultStore';
import { useNotificationStore } from '../../store/notificationStore';
import { detectVaultImportSource, parseApibooJson, dedupeKey } from '../../utils/vaultPortability';
import { IMPORT_SOURCES, sealedExportContent, exportErrorMessage, existingEnvelopeKey } from './vaultImportExportHelpers';

/** Import: source choice, parsing a file, unsealing a sealed export and running the import. */
export function useVaultImport({
  t,
  sealed,
  onClose,
  fileInputRef,
  sourceKey,
  setSourceKey,
  fileName,
  setFileName,
  parsed,
  setParsed,
  setSealed,
  sealedPassword,
  setSealedPassword,
  unsealing,
  setUnsealing,
  skipDupes,
  importing,
  setImporting,
  setProgress,
  setError,
}) {
  // A parsed preview belongs to one source format; drop it when the source
  // changes so a stale count from a different file can't be imported. Same for
  // a pending encrypted file — its password prompt must not outlive the file.
  const changeSource = (key) => {
    setSourceKey(key);
    setParsed(null);
    setSealed(null);
    setSealedPassword('');
    setFileName('');
    setError(null);
  };

  const typeCounts = useMemo(() => {
    const counts = {};
    for (const it of (parsed && parsed.items) || []) {
      counts[it.type] = (counts[it.type] || 0) + 1;
    }
    return counts;
  }, [parsed]);

  const applyParsed = (name, result) => {
    setFileName(name);
    setParsed(result);
    setError(null);
  };

  /**
   * Parse `text` with the SELECTED source, then rescue the two silent-failure
   * cases that were reported as "import is not working":
   *
   *   - an EMPTY file (LastPass's export flow opens the CSV in a browser tab;
   *     a mis-saved page is 0 bytes) previously showed the bland "nothing to
   *     import" — now it says the file is empty, which is actionable;
   *   - the WRONG DROPDOWN (two sources are ".csv", two are ".json") fed a
   *     LastPass file to the Bitwarden parser and yielded zero items with no
   *     explanation. If the chosen parser finds nothing, detection re-asks the
   *     CONTENT — and its answer is only believed if re-parsing with it
   *     actually produces items, so a wrong guess cannot make things worse.
   *
   * An ENCRYPTED export short-circuits all of that: there is nothing to parse
   * until Rust has unsealed it, so the file parks in `sealed` and the UI asks
   * for its password instead of showing a preview.
   */
  const parseText = (name, text) => {
    if (String(text == null ? '' : text).trim() === '') {
      setParsed(null);
      setSealed(null);
      setFileName(name);
      setError(t('vaultIO.emptyFile'));
      return;
    }
    const sealedContent = sealedExportContent(text);
    if (sealedContent != null) {
      setFileName(name);
      setParsed(null);
      setSealed({ content: sealedContent });
      setSealedPassword('');
      setError(null);
      return;
    }
    setSealed(null);
    const result = IMPORT_SOURCES[sourceKey].parse(text);
    if (result.items.length === 0) {
      const detected = detectVaultImportSource(text);
      if (detected && detected !== sourceKey) {
        const better = IMPORT_SOURCES[detected].parse(text);
        if (better.items.length > 0) {
          setSourceKey(detected);
          applyParsed(name, better);
          return;
        }
      }
    }
    applyParsed(name, result);
  };

  const pickFile = async () => {
    setError(null);
    const source = IMPORT_SOURCES[sourceKey];
    if (isTauri()) {
      try {
        const { open } = await import('@tauri-apps/plugin-dialog');
        const { readTextFile } = await import('@tauri-apps/plugin-fs');
        const selected = await open({
          multiple: false,
          filters: [{ name: t('vaultIO.importFileFilter'), extensions: source.ext }],
        });
        if (selected == null) return;
        const path = Array.isArray(selected) ? selected[0] : selected;
        const text = await readTextFile(path);
        parseText(String(path).split(/[\\/]/).pop() || String(path), text);
      } catch (e) {
        setError(String((e && e.message) || e));
      }
      return;
    }
    fileInputRef.current?.click();
  };

  const onFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        parseText(file.name, String(reader.result || ''));
      } catch (err) {
        setError(String((err && err.message) || err));
      }
    };
    reader.onerror = () => setError(t('vaultIO.readError'));
    reader.readAsText(file);
  };

  /**
   * Decrypt a pending encrypted export with its file password, host-side, and
   * feed the plaintext through the normal Apiboo parser so the preview,
   * dedupe, and passkey handling behave exactly as for a plain file.
   */
  const runUnseal = async () => {
    if (!sealed || unsealing || sealedPassword === '') return;
    setUnsealing(true);
    setError(null);
    try {
      const unsealed = await invoke('vault_export_unseal', {
        content: sealed.content,
        password: sealedPassword,
      });
      // Contract: a JSON string. Tolerate an already-parsed shape so a drift
      // in the parallel Rust work degrades to a working import, not a crash.
      const text = typeof unsealed === 'string' ? unsealed : JSON.stringify(unsealed);
      const result = parseApibooJson(text);
      setSourceKey('apibooJson');
      setSealed(null);
      setSealedPassword('');
      applyParsed(fileName, result);
    } catch (e) {
      // `export_password_wrong` still maps to ONE message covering wrong
      // password AND damaged file — the crypto side returns the same failure
      // for both (no oracle), so pretending to know which it was would be a
      // guess. The other tokens are determinable facts about the FILE, not
      // about the password (`export_unsupported` = not an export this build
      // opens, `export_corrupt` = structurally broken), so saying which costs
      // nothing and saves the user retyping a password that was never the
      // problem. Anything unmapped keeps today's wording.
      setError(
        exportErrorMessage(
          t,
          e,
          'sealedWrongPassword',
          'Wrong password for this file, or the file is damaged.'
        )
      );
    } finally {
      setUnsealing(false);
    }
  };

  const runImport = async () => {
    if (!parsed || !parsed.items.length || importing) return;
    setImporting(true);
    setError(null);
    const items = parsed.items;
    setProgress({ done: 0, total: items.length });
    // key → existing item id. The id is what makes a deduped entry still able
    // to contribute its passkeys to the item it matched (see below).
    const seen = new Map();
    if (skipDupes) {
      for (const env of useVaultStore.getState().items) seen.set(existingEnvelopeKey(env), env.id);
    }
    let imported = 0;
    let deduped = 0;
    let failed = 0;
    let passkeysImported = 0;
    let passkeysSkipped = 0;

    // Passkeys cannot ride along in `vault_put_item` — the Rust side refuses
    // any payload that introduces credential material, which is what stops a
    // hostile import from injecting a key the sender knows. So the item is
    // saved first and its credentials are added by the one command allowed to.
    // Never fatal: the item is already safely stored by this point.
    const attachPasskeys = async (itemId, credentials) => {
      if (!itemId || !Array.isArray(credentials) || credentials.length === 0) return;
      try {
        const report = await invoke('passkey_import', { itemId, credentials });
        passkeysImported += (report && report.imported) || 0;
        passkeysSkipped += (report && report.skipped) || 0;
      } catch {
        passkeysSkipped += credentials.length;
      }
    };

    try {
      // Plans v2: the NEW items (duplicates that will be skipped do not count)
      // have to fit the plan's DB connection / host caps, or nothing is
      // imported: the error line and the upgrade say why.
      const keys = new Set(seen.keys());
      const fresh = [];
      for (const it of items) {
        if (skipDupes) {
          const key = dedupeKey(it);
          if (keys.has(key)) continue;
          keys.add(key);
        }
        fresh.push({ type: it.type });
      }
      await guardNewVaultItems(fresh);
      for (let idx = 0; idx < items.length; idx += 1) {
        const it = items[idx];
        if (skipDupes) {
          const key = dedupeKey(it);
          if (seen.has(key)) {
            deduped += 1;
            // The item is a duplicate; its passkeys may not be. Re-importing an
            // export after the credentials were added to it is the normal way
            // to reach here, and refusing would leave the user with no way to
            // pick them up short of deleting the item first. `passkey_import`
            // dedupes by credentialId and honours tombstones, so this cannot
            // double-add or resurrect a deleted credential.
            await attachPasskeys(seen.get(key), it.passkeys);
            setProgress({ done: idx + 1, total: items.length });
            continue;
          }
          seen.set(key, null);
        }
        // Tolerate a single rejected item (e.g. a backend validation error) so
        // one bad row can't abort the whole batch — count it and keep going.
        try {
          const env = await invoke('vault_put_item', {
            item: { type: it.type, name: it.name, favorite: !!it.favorite, data: it.data || {} },
          });
          imported += 1;
          const newId = env && env.id;
          if (skipDupes) seen.set(dedupeKey(it), newId);
          await attachPasskeys(newId, it.passkeys);
        } catch {
          failed += 1;
          if (Array.isArray(it.passkeys)) passkeysSkipped += it.passkeys.length;
        }
        setProgress({ done: idx + 1, total: items.length });
      }
      await useVaultStore.getState().refresh();
      const summary = t(failed > 0 ? 'vaultIO.importedToastFailed' : 'vaultIO.importedToast', {
        count: imported,
        skipped: deduped,
        failed,
      });
      // Only mentioned when the file actually carried credentials, so an
      // ordinary password import reads exactly as it did before.
      const passkeyLine =
        passkeysImported || passkeysSkipped
          ? ` ${t(
              passkeysSkipped > 0
                ? 'vaultIO.importedPasskeysPartial'
                : 'vaultIO.importedPasskeys',
              { count: passkeysImported, skipped: passkeysSkipped }
            )}`
          : '';
      useNotificationStore.getState().success(`${summary}${passkeyLine}`);
      onClose();
    } catch (e) {
      setError(String((e && e.message) || e));
      setImporting(false);
    }
  };

  return { changeSource, typeCounts, pickFile, onFileInputChange, runUnseal, runImport };
}
